/**
 * Vue i18n instance and locale management
 * - Creates i18n with English as default
 * - Lazy-loads non-English locales on demand
 * - Exported for use in main.ts and stores
 */

import { createI18n } from 'vue-i18n';
import en from './en';
import es from './es';
import fr from './fr';
import de from './de';
import it from './it';
import sl from './sl';

// Map of all available locales
const localeMessages = {
  en,
  es,
  fr,
  de,
  it,
  sl,
};

/** Every locale this build pre-loads. */
export type Locale = keyof typeof localeMessages;

/**
 * The catalogues in the shape vue-i18n's options demand: every locale carrying
 * the full `en` schema.
 *
 * They are partial by design — `es`, `fr`, `de` and `it` do not define every
 * section `en` does (the `enroll` and `panelApp` sections are missing there
 * today), and a key a locale does not define falls back to `en` per key at
 * runtime, which is what `fallbackLocale: 'en'` is for; `localeAudit.ts` audits
 * the keys the *code* reads, not catalogue completeness. This one assertion
 * states that, and replaces the `any` that used to sit on the whole options
 * object (card t_016c7a0e).
 */
const catalogues = localeMessages as unknown as Record<Locale, typeof en>;

/**
 * The instance type, inferred from the factory's own options — writing the
 * vue-i18n generics by hand (`I18n<Messages, DateTimeFormats, NumberFormats,
 * Locale, Legacy>`) is a guessing game.
 */
export type LocaleI18n = ReturnType<typeof createI18nInstance>;

function createI18nInstance() {
  return createI18n({
    legacy: false, // Composition API mode
    locale: 'en', // Default locale
    fallbackLocale: 'en', // Fallback if key not found
    messages: catalogues, // All locales pre-loaded
  });
}

let i18n: LocaleI18n | null = null;

/**
 * Load a locale and set it in i18n
 * All locales are pre-imported to avoid Vite bundler issues
 */
export async function loadLocale(locale: string): Promise<void> {
  if (!i18n) {
    console.warn('i18n not initialized yet');
    return;
  }

  const instance = i18n;

  if (instance.global.locale.value === locale) {
    // Already loaded
    return;
  }

  const messages = catalogues[locale as Locale];
  if (!messages) {
    console.warn(`Locale '${locale}' not found, falling back to English`);
    instance.global.locale.value = 'en';
    return;
  }

  instance.global.setLocaleMessage(locale as Locale, messages);
  instance.global.locale.value = locale as Locale;
}

/**
 * Initialize and export i18n instance
 * Call this in main.ts with app.use(i18n)
 */
export function createLocaleI18n(): LocaleI18n {
  i18n = createI18nInstance();

  return i18n;
}

/**
 * Get the current i18n instance
 * Use in stores/services where you need to access i18n outside of components
 */
export function getI18n(): LocaleI18n {
  if (!i18n) {
    throw new Error('i18n not initialized. Call createLocaleI18n() first.');
  }
  return i18n;
}

export default i18n;
